import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'

interface ScrapePayload {
  source: { url: string; kind: 'home' | 'profile' | 'search' | 'list' | 'bookmarks' | 'tweet' | 'other' }
  tweets: { tweetUrl: string; tweetText: string; authorHandle: string }[]
}

function notifyOpener(data: Record<string, unknown>): boolean {
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ bluebird: true, ...data }, '*')
      return true
    }
  } catch {
    /* ignore cross-origin access errors */
  }
  return false
}

export default function BookmarkletIngest() {
  const navigate = useNavigate()
  const [state, setState] = useState<'sending' | 'ok' | 'error'>('sending')
  const [error, setError] = useState('')
  const [batchId, setBatchId] = useState<string | null>(null)

  useEffect(() => {
    const encoded = window.location.hash.slice(1)
    if (!encoded) {
      setState('error')
      setError('No bookmarklet data in the URL.')
      notifyOpener({ ok: false, error: 'missing payload' })
      return
    }

    let payload: ScrapePayload
    try {
      const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
      const padded = b64 + '==='.slice((b64.length + 3) % 4)
      const bin = atob(padded)
      const json = decodeURIComponent(escape(bin))
      payload = JSON.parse(json)
    } catch {
      setState('error')
      setError('Could not decode the payload.')
      notifyOpener({ ok: false, error: 'decode failed' })
      return
    }

    if (!payload.tweets || payload.tweets.length === 0) {
      setState('error')
      setError('No tweets in the payload.')
      notifyOpener({ ok: false, error: 'empty payload' })
      return
    }

    const apiKind =
      payload.source.kind === 'tweet' ? 'other' : payload.source.kind

    api
      .scrapeTweets({
        source: { url: payload.source.url, kind: apiKind },
        tweets: payload.tweets,
      })
      .then((res) => {
        setBatchId(res.batchId)
        setState('ok')
        const notified = notifyOpener({ ok: true, batchId: res.batchId, count: res.count })
        if (notified) {
          // Opener will close us; but add a safety close in case the opener
          // never got the message (e.g. user navigated away on x.com).
          setTimeout(() => {
            try { window.close() } catch { /* ignore */ }
          }, 400)
        }
      })
      .catch((err) => {
        setState('error')
        const msg = err instanceof ApiError ? err.message : 'send failed'
        setError(msg)
        notifyOpener({ ok: false, error: msg })
      })
  }, [])

  // If the opener closed us, nothing renders. This UI only matters when the
  // user opened the URL manually (no opener) or the flow failed.
  return (
    <div className="mx-auto flex max-w-md flex-col items-center py-20 text-center">
      {state === 'sending' && (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="mt-4 text-sm text-muted-foreground">Sending to Bluebird…</p>
        </>
      )}
      {state === 'ok' && (
        <>
          <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          <p className="mt-4 text-sm">Captured. You can close this tab.</p>
          {batchId && (
            <Button asChild className="mt-4" size="sm" variant="secondary" onClick={() => navigate(`/app/engagement?batch=${batchId}`)}>
              <Link to={`/app/engagement?batch=${batchId}`}>View batch</Link>
            </Button>
          )}
        </>
      )}
      {state === 'error' && (
        <>
          <AlertCircle className="h-6 w-6 text-destructive" />
          <h1 className="mt-4 text-lg font-semibold">Could not ingest</h1>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <Button asChild className="mt-4" size="sm" variant="secondary">
            <Link to="/app/engagement">Back to engagement</Link>
          </Button>
        </>
      )}
    </div>
  )
}
